import { gql, useLazyQuery } from "@apollo/client";
import { Card, CircularProgress, Fade, Grid, Icon, Modal, Skeleton, Tooltip } from "@mui/material";
import cover from "assets/images/cover_caza.png";
import logo from "assets/images/logo.png";
import logo_alt from "assets/images/logo_alt.png";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "50%", xxl: "50%" },
  overflowY: "auto",
};

export default function ModalPermisoCazaPDF({ open, handleClose, idPermiso }) {
  const [file, setFile] = useState(null);

  const [getPermiso, { data, loading }] = useLazyQuery(gql`
    query getPermiso($id: ID!) {
      permisoGob(id: $id) {
        id
        tipo
        fechaVencimiento
        estado
        anioPermiso
        nroPermiso
        fechaInicio
        url
        acompaniante
        usuario {
          nombre
          apellido
          username
          dni
          email
        }
        comprobantesPago {
          id
          nombre
          file
          estado
          tipo
        }
      }
    }
  `);

  const generatePDF = () => {
    const element = document.getElementById("certificado");
    const originalWidth = element.style.width;
    const originalHeight = element.style.height;

    element.style.width = "794px"; // Ancho en píxeles para A4
    element.style.height = "1123px"; // Alto en píxeles para A4
    element.style.overflow = "visible"; // Evita recortes
    setTimeout(() => {
      if (element) {
        html2canvas(element, { scale: 1 }).then((canvas) => {
          const imgData = canvas.toDataURL("image/jpeg", 1);

          const report = new jsPDF({
            orientation: "portrait",
            compress: true,
          });

          const pdfWidth = report.internal.pageSize.getWidth();
          const pdfHeight = report.internal.pageSize.getHeight();

          report.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
          report.save(
            `Permiso ${data?.permisoGob?.anioPermiso}-${data?.permisoGob?.nroPermiso}${
              data?.permisoGob?.estado === "Aprobado" ? "" : " Temporal"
            }.pdf`
          );
          element.style.width = originalWidth;
          element.style.height = originalHeight;
        });
      }
    }, 500);
  };

  useEffect(() => {
    if (open) {
      getPermiso({ variables: { id: idPermiso } });
    } else {
      setFile(null);
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3} pb={0}>
              <SoftBox>
                <SoftTypography variant="h6">
                  {loading ? (
                    <Skeleton variant="text" width={100} />
                  ) : (
                    `Permiso Nº ${data?.permisoGob?.anioPermiso} - ${data?.permisoGob?.nroPermiso}`
                  )}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                <SoftBox display="flex" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon
                      fontSize="medium"
                      onClick={() => {
                        handleClose();
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox id="certificado" p={3}>
              <SoftBox position="relative">
                <SoftBox
                  display="flex"
                  alignItems="top"
                  position="relative"
                  minHeight="18vh"
                  borderRadius="xl"
                  sx={{
                    backgroundImage: ({
                      functions: { rgba, linearGradient },
                      palette: { gradients },
                    }) =>
                      `${linearGradient(
                        rgba(gradients.primary.main, 0.6),
                        rgba(gradients.primary.state, 0.6)
                      )}, url(${cover})`,
                    backgroundSize: "cover",
                    backgroundPosition: "50%",
                    overflow: "hidden",
                  }}
                >
                  <Grid container justifyContent="center" p={4} mb={4} alignItems={"center"}>
                    <Grid item xs={12} sm={4}>
                      <SoftBox
                        component="img"
                        src={logo_alt}
                        alt="Gobierno de la Provincia de San Luis Logo"
                        width="100%"
                        p={3}
                      />
                    </Grid>
                  </Grid>
                </SoftBox>
                <Card
                  sx={{
                    backdropFilter: `saturate(200%) blur(30px)`,
                    backgroundColor: ({ functions: { rgba }, palette: { white } }) =>
                      rgba(white.main, 1),
                    boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
                    position: "relative",
                    mt: -8,
                    mx: 2,
                    py: 2,
                    px: 2,
                  }}
                >
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="light">
                        Número de permiso
                      </SoftTypography>
                      <SoftTypography variant="h6" fontWeight="bold">
                        {loading ? (
                          <Skeleton variant="text" width={100} />
                        ) : (
                          `${data?.permisoGob?.anioPermiso} - ${data?.permisoGob?.nroPermiso}${
                            data?.permisoGob?.estado === "Aprobado" ? "" : "T"
                          }`
                        )}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox textAlign="right">
                      {/* <SoftTypography variant="h6" fontWeight="light">
                        Fecha de emisión
                      </SoftTypography>
                      <SoftTypography variant="h6" fontWeight="bold">
                        {loading ? (
                          <Skeleton variant="text" width={100} />
                        ) : (
                          dayjs(data?.permisoGob?.fechaInicio).format("DD/MM/YYYY")
                        )}
                      </SoftTypography> */}
                    </SoftBox>
                  </SoftBox>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                      <SoftTypography variant="h5" fontWeight="bold" textAlign="center">
                        {loading ? (
                          <Skeleton variant="text" width={100} />
                        ) : (
                          `${data?.permisoGob?.tipo}`
                        )}
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <SoftTypography
                        variant="h6"
                        fontWeight="light"
                        textAlign={{
                          sm: "left",
                          xs: "center",
                        }}
                      >
                        Nombre y Apellido:{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {makeNiceText(
                            `${data?.permisoGob?.usuario?.apellido} ${data?.permisoGob?.usuario?.nombre}`,
                            "nombre"
                          )}
                        </span>
                        <br />
                        DNI:{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {formatMoneyPunto(data?.permisoGob?.usuario?.dni)}
                        </span>
                        <br />
                        Fecha de emisión:{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {dayjs(data?.permisoGob?.fechaInicio).format("DD/MM/YYYY")}
                        </span>
                        <br />
                        Establecimiento/s habilitado/s para cazar:{" "}
                        {data?.permisoGob?.comprobantesPago
                          ?.filter(
                            (comprobante) =>
                              comprobante.tipo === "Permiso de campo" &&
                              comprobante.estado === "Aprobado"
                          )
                          .map((comprobante) => (
                            <SoftBox
                              key={comprobante.id}
                              display="flex"
                              alignItems="center"
                              gap={1}
                            >
                              <Icon>check</Icon>
                              <SoftTypography variant="h6" fontWeight="bold">
                                {comprobante.nombre}
                              </SoftTypography>
                            </SoftBox>
                          ))}
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <SoftTypography
                        variant="h6"
                        fontWeight="light"
                        textAlign={{
                          sm: "left",
                          xs: "center",
                        }}
                      >
                        Permiso válido sólo con el carnet de ANMaC habilitado, DNI y permiso del
                        propietario/administrador del campo.
                        <br />
                        El portador de la presente licencia declara conocer el contenido de la
                        <span style={{ fontWeight: "bold" }}> Resolucion N° 1-DB-SAyDS-2025</span>
                      </SoftTypography>
                    </Grid>
                    {/* <Grid item xs={12}>
                      <SoftBox>
                        <SoftTypography variant="h6" fontWeight="bold">
                          Campos autorizados
                        </SoftTypography>
                        {data?.permisoGob?.comprobantesPago
                          ?.filter(
                            (comprobante) =>
                              comprobante.tipo === "Permiso de campo" &&
                              comprobante.estado === "Aprobado"
                          )
                          .map((comprobante) => (
                            <SoftBox
                              key={comprobante.id}
                              display="flex"
                              alignItems="center"
                              gap={1}
                            >
                              <Icon>check</Icon>
                              <SoftTypography variant="h6" fontWeight="light">
                                {comprobante.nombre}
                              </SoftTypography>
                            </SoftBox>
                          ))}
                      </SoftBox>
                    </Grid> */}
                  </Grid>
                  <SoftBox display="flex" justifyContent="space-between" mt={3} alignItems="center">
                    <SoftBox
                      pl={2}
                      component="img"
                      src={logo}
                      alt="Gobierno de la Provincia de San Luis Logo"
                      width="160px"
                    />
                    {!loading ? (
                      <Card>
                        <SoftBox p={2} display="flex" justifyContent="center" alignItems="center">
                          <QRCode
                            size={90}
                            style={{ height: "auto", maxWidth: "90px", width: "90px" }}
                            value={`${window.location.origin}/control-permisos/${data?.permisoGob?.url}`}
                            viewBox={`0 0 90 90`}
                          />
                        </SoftBox>
                      </Card>
                    ) : (
                      <Card>
                        <SoftBox p={2} display="flex" justifyContent="center" alignItems="center">
                          <Skeleton
                            variant="rectangular"
                            sx={{
                              borderRadius: "10px",
                            }}
                            width={90}
                            height={90}
                          />
                        </SoftBox>
                      </Card>
                    )}
                  </SoftBox>
                  <SoftBox display="flex" justifyContent="center" mt={3} alignItems="center">
                    <SoftBox textAlign="center">
                      <SoftTypography variant="caption" fontWeight="light">
                        Powered by&nbsp;
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Gobierno de la Provincia de San Luis
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </Card>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3} pt={0}>
              <SoftButton color="primary" onClick={generatePDF} fullWidth>
                {false ? (
                  <CircularProgress
                    size={15}
                    sx={{
                      marginRight: 0.5,
                    }}
                    color="white"
                  />
                ) : (
                  <Icon>download</Icon>
                )}
                &nbsp;Descargar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalPermisoCazaPDF.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPermiso: PropTypes.string,
};
